import React, { useEffect, useState } from 'react';
import './ShopsTable.css';

function ShopsTable({ onShopSelect, shops: propShops }) {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(!propShops);
  const [sortBy, setSortBy] = useState('average');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    if (propShops) {
      setShops(propShops);
    } else {
      fetchShops();
    }
  }, [propShops]);

  const fetchShops = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/stores');
      const data = await res.json();
      setShops(data);
    } catch (err) {
      console.error('Error fetching shops:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getAverageRating = (shop) => {
    const ratings = [];
    
    const trustpilotRating = shop.externalRatings?.find(r => r.source === 'trustpilot')?.rating;
    if (trustpilotRating) ratings.push(trustpilotRating);
    
    const trustedShopsRating = shop.externalRatings?.find(r => r.source === 'trustedshops')?.rating;
    if (trustedShopsRating) ratings.push(trustedShopsRating);
    
    const dlRating = shop.dlCommunityRating?.average;
    if (dlRating) ratings.push(dlRating);
    
    if (ratings.length === 0) return 0;
    return ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
  };

  const getSortedShops = () => {
    const sorted = [...shops].sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'name':
          aVal = a.name || '';
          bVal = b.name || '';
          break;
        case 'trustpilot':
          aVal = a.externalRatings?.find(r => r.source === 'trustpilot')?.rating || 0;
          bVal = b.externalRatings?.find(r => r.source === 'trustpilot')?.rating || 0;
          break;
        case 'trustedshops':
          aVal = a.externalRatings?.find(r => r.source === 'trustedshops')?.rating || 0;
          bVal = b.externalRatings?.find(r => r.source === 'trustedshops')?.rating || 0;
          break;
        case 'dlcommunity':
          aVal = a.dlCommunityRating?.average || 0;
          bVal = b.dlCommunityRating?.average || 0;
          break;
        case 'average':
          aVal = getAverageRating(a);
          bVal = getAverageRating(b);
          break;
        default:
          aVal = a.name;
          bVal = b.name;
      }

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      } else {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
    });

    return sorted;
  };

  const renderSortIcon = (column) => {
    if (sortBy !== column) return <span className="sort-icon"> ⇅</span>;
    return <span className="sort-icon">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>;
  };

  const renderRating = (rating, count, url, source) => {
    if (!rating) return <span className="no-rating">—</span>;
    
    const sourceLabel = {
      trustpilot: 'Auf Trustpilot ansehen',
      trustedshops: 'Auf Trusted Shops ansehen',
      google: 'Google Bewertung'
    }[source] || 'Bewertung ansehen';
    
    const ratingContent = (
      <>
        <span className="rating-number">{rating.toFixed(1)}</span>
        <span className="rating-star">★</span>
        {count > 0 && <span className="rating-count">({count})</span>}
      </>
    );
    
    // Wenn URL vorhanden, als Link rendern
    if (url) {
      return (
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="rating-link"
          title={sourceLabel}
          onClick={(e) => e.stopPropagation()}
        >
          {ratingContent}
        </a>
      );
    }
    
    return ratingContent;
  };

  const renderDLRating = (shop) => {
    const rating = shop.dlCommunityRating?.average;
    const count = shop.dlCommunityRating?.count;
    
    if (!rating) {
      return (
        <button 
          className="btn-first-rating"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onShopSelect?.(shop);
          }}
          type="button"
          title="Klick um die erste Bewertung zu schreiben"
        >
          <span className="btn-text-full">🌟 Sei der Erste</span>
          <span className="btn-text-short">⭐</span>
        </button>
      );
    }
    return (
      <button
        className="btn-first-rating"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onShopSelect?.(shop);
        }}
        type="button"
        title="Klick um zur Bewertungsseite zu gehen"
        style={{ background: 'transparent', boxShadow: 'none', padding: 0 }}
      >
        <span className="rating-number">{rating.toFixed(1)}</span>
        <span className="rating-star">★</span>
        <span className="rating-count">({count})</span>
      </button>
    );
  };

  const sortedShops = getSortedShops();

  if (loading) {
    return <div className="shops-table-container"><p>Lädt Shops...</p></div>;
  }

  return (
    <div className="shops-table-container">
      <div className="shops-table-wrapper">
        <table className="shops-table">
          <thead>
            <tr>
              <th className="col-logo">Logo</th>
              <th 
                className="col-name sortable"
                onClick={() => handleSort('name')}
              >
                Shop Name {renderSortIcon('name')}
              </th>
              <th className="col-average sortable" onClick={() => handleSort('average')}>⌀{renderSortIcon('average')}</th>
              <th 
                className="col-trustpilot sortable"
                onClick={() => handleSort('trustpilot')}
              >
                Trustpilot {renderSortIcon('trustpilot')}
              </th>
              <th 
                className="col-trustedshops sortable"
                onClick={() => handleSort('trustedshops')}
              >
                Trusted Shops {renderSortIcon('trustedshops')}
              </th>
              <th 
                className="col-dlcommunity sortable"
                onClick={() => handleSort('dlcommunity')}
              >
                Dichtlicht {renderSortIcon('dlcommunity')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedShops.map(shop => {
              // Prüfe auf Trustpilot und Trusted Shops Ratings separat
              const trustpilotRating = shop.externalRatings?.find(r => r.source === 'trustpilot');
              const trustedShopsRating = shop.externalRatings?.find(r => r.source === 'trustedshops');
              const dlRating = shop.dlCommunityRating;

              return (
                <tr 
                  key={shop._id} 
                  className="shop-row"
                >
                  <td className="col-logo" onClick={() => onShopSelect?.(shop)} style={{ cursor: 'pointer' }}>
                    {shop.logo ? (
                      <img src={shop.logo} alt={shop.name} className="shop-logo-thumb" />
                    ) : (
                      <span className="no-logo">🏪</span>
                    )}
                  </td>
                  <td className="col-name">
                    {shop.website ? (
                      <a 
                        href={shop.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="shop-name-link"
                        title={shop.website}
                      >
                        <strong>{shop.name}</strong>
                      </a>
                    ) : (
                      <strong style={{ cursor: 'pointer' }} onClick={() => onShopSelect?.(shop)}>{shop.name}</strong>
                    )}
                  </td>
                  <td className="col-average">
                    <span className="rating-number">{getAverageRating(shop).toFixed(1)}</span>
                    <span className="rating-star">★</span>
                  </td>
                  <td className="col-trustpilot">
                    {renderRating(trustpilotRating?.rating, trustpilotRating?.reviewCount, trustpilotRating?.url, trustpilotRating?.source)}
                  </td>
                  <td className="col-trustedshops">
                    {renderRating(trustedShopsRating?.rating, trustedShopsRating?.reviewCount, trustedShopsRating?.url, trustedShopsRating?.source)}
                  </td>
                  <td className="col-dlcommunity">
                    {renderDLRating(shop)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {sortedShops.length === 0 && (
        <div className="no-shops">
          <p>Keine Shops gefunden</p>
        </div>
      )}
    </div>
  );
}

export default ShopsTable;
